<template>
  <div
    v-if="config"
    :class="[
      'rb',
      `rb-${currentStepId}`,
      {
        'rb-engagement': mode === 'engagement',
        'rb-show-gallery': showGallery,
        'rb-show-fullscreen': showFullscreen,
        'rb-show-step-fullscreen': showStepFullscreen,
        'rb-show-footer': showFooter,
        'rb-is-safari': isSafari,
      },
    ]"
  >
    <popup-share id="rb-popup-share" />
    <popup-gia id="rb-popup-gia" />
    <popup-exit id="rb-popup-exit" />
    <popup-filters
      id="rb-popup-filters"
      :filters="config.filters"
      :applied-filters="appliedFilters"
    />
    <popup-static
      v-for="(popup, indexPopup) in config.popups"
      :key="`static-popup-${indexPopup}`"
      :id="popup.popup_id"
      :data="popup"
    />
    <builder-header
      :current-step="currentStep"
      :total-steps="totalSteps"
      :logo="config.logo"
    />
    <div class="rb-main">
      <media-gallery
        :data="config"
        :current-step="currentStep"
        :selected-options="selectedOptions"
        :api-model-viewer="apiModelViewer"
        :available-models="availableModels"
      />
      <steps-manager
        :config="config"
        :current-step="currentStep"
        :selected-options="selectedOptions"
        :diamonds-data="{ diamonds }"
        :diamonds-meta="diamondsMeta"
        :complete="allComplete"
        :confirmed="reachedConfirm"
        :availability="availability"
      />
      <product-info
        :data="config"
        :current-step="currentStep"
        :selected-options="selectedOptions"
        :complete="allComplete"
        :availability="availability"
        :render-init-diamond-price="renderInitDiamondPrice"
        :render-diamond-price="renderDiamondPrice"
      />
      <diamond-explorer
        :data="{
          id: 'diamond',
          label: 'Explore Diamonds',
          type: 'diamond-selector',
          countStep: true,
          popupId: 'rb-popup-diamond-guide',
        }"
        :selected-options="selectedOptions"
        :diamonds-data="{ diamonds }"
        :diamonds-meta="diamondsMeta"
        :config="config"
      />
    </div>
  </div>
</template>

<style>
@import url("https://jeandousset.com/cdn/shop/t/174/assets/base.css?v=44622871468228552191722548986");
@import "./assets/main.css";
@import "../node_modules/keen-slider/keen-slider.css";
@import url("https://staticw2.yotpo.com/pLTSIlnEUIlovytGZPwf3lQYIwszGrkOiPBjoX1i/widget.css?widget_version=2023-03-01_09-17-08");
</style>

<script>
import BuilderHeader from "@/components/BuilderHeader";
import MediaGallery from "@/components/MediaGallery";
import StepsManager from "@/components/StepsManager";
import DiamondExplorer from "@/components/DiamondExplorer";
import ProductInfo from "@/components/ProductInfo";
import PopupFilters from "@/components/PopupFilters";
import PopupStatic from "@/components/PopupStatic";
import PopupShare from "@/components/PopupShare";
import PopupGia from "@/components/PopupGia";
import PopupExit from "@/components/PopupExit";
import CommMixin from "@/mixins/CommMixin";
import PaginationMixin from "@/mixins/PaginationMixin";
import DeduplicationMixin from "@/mixins/DeduplicationMixin";
import {
  getBaseVariantPrice,
  fetchDiamonds,
  deleteKey,
  saveForLater,
  getSavedForLater,
  deleteSavedForLater,
  getAvailableVariants,
  checkFavorites,
  checkAvailable3dModels,
  getAvailable3dModels,
  getQueryVariable,
  getSelectedVariants,
  createVariantUrl,
  formatMoney,
  defaultOptions,
} from "./lib";
export default {
  mixins: [CommMixin, PaginationMixin, DeduplicationMixin],
  components: {
    BuilderHeader,
    MediaGallery,
    StepsManager,
    ProductInfo,
    PopupFilters,
    PopupStatic,
    PopupShare,
    PopupGia,
    PopupExit,
    DiamondExplorer,
  },
  props: {
    mode: {
      type: String,
      default: "engagement",
    },
  },
  data: function () {
    return {
      config: null,
      currentStep: 0,
      previousStep: -1,
      selectedOptions: [],
      showGallery: true,
      showFullscreen: false,
      showStepFullscreen: false,
      showFooter: true,
      diamonds: [],
      diamondsMeta: null,
      fromDiamondPrice: 0,
      baseVariantPrice: 0,
      sortMode: { type: "retail_price", mode: "asc" },
      reachedConfirm: false,
      comesFromSaveForLater: false,
      appliedFilters: [],
      availability: [],
      availableModels: [],
      renderInitDiamondPrice: false,
      renderDiamondPrice: "",
    };
  },
  computed: {
    isSafari: function () {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },
    apiModelViewer: function () {
      return this.config.api.modelViewer;
    },
    countableSteps: function () {
      return this.config.steps.filter((step) => step.countStep);
    },
    currentStepId: function () {
      return this.config.steps[this.currentStep]?.id;
    },
    totalSteps: function () {
      return this.countableSteps.length;
    },
    baseRingPrice: function () {
      return this.baseVariantPrice / 100;
    },
    allComplete: function () {
      var allComplete = this.countableSteps.reduce(
        (complete, step) =>
          complete &&
          (this.selectedOptions.find(
            (selectedOption) => selectedOption.step.id === step.id
          )
            ? true
            : false),
        true
      );
      // console.log("allComplete", allComplete);
      //console.log("allComplete", allComplete);
      return allComplete;
    },
    baseModel: function () {
      return (
        this.config.product.tags
          .find((tag) => tag.indexOf("model-") > -1)
          ?.split("-")
          ?.slice(1)
          .join("-") || ""
      );
    },
  },
  watch: {
    currentStep: function () {
      this.updateLayout();
      if (this.currentStep === 6) {
        this.reachedConfirm = true;
        this.broadcast("rb-reset-listing-mode");
      }
    },
    fromDiamondPrice: function () {
      this.reloadTotalPrice();
    },
    selectedOptions: function (val, oldVal) {
      var oldShape = oldVal.find(
        (selectedOption) => selectedOption.step.id === "shape"
      );
      var currentShape = val.find(
        (selectedOption) => selectedOption.step.id === "shape"
      );
      if (oldShape?.choice?.id !== currentShape?.choice?.id) {
        this.paginationHardReset();
        // console.log('resetting');
        this.selectedOptions = this.selectedOptions.filter(
          (selectedOption) => selectedOption.step.id !== "diamond"
        );
        this.reachedConfirm = false;
        this.clearDiamonds();
        this.loadFromDiamondPrice();
      }
      this.$nextTick(() => {
        this.broadcast("rb-selected-options-updated", {
          selectedOptions: this.selectedOptions,
          mappings: this.config.mappings,
        });
      });

      this.updateQueryVariant();
    },
  },
  mounted: function () {
    // This is needed in order to expose methods as part of the WC spec
    this.$el.init = this.init;
    this.$el.isPrevWhishlist = this.isPrevWhishlist;
    this.$el.isSoldOut = this.isSoldOut;
    this.$el.updateInstallments = this.updateInstallments;
    this.$el.startBuilder = this.startBuilder;
    this.$emit("rb-mounted");

    this.baseVariantPrice = this.config.product.price_min;

    var selectedVariantId = getQueryVariable("variant");

    this.initEvents();
    this.updateLayout();

    ///this.testApi();

    if (!selectedVariantId) {
      this.selectDefaultShape();
      this.loadFromDiamondPrice();
    } else {
      this.checkSelectedVariant();
      this.checkSelectedDiamond();
    }

    setTimeout(() => {
      this.broadcast("rb-update-price", {
        baseRingPrice: this.baseRingPrice,
        fromDiamondPrice: this.fromDiamondPrice,
        selectedOptions: this.selectedOptions,
      });
    }, 500);

    this.checkSavedForLater();

    var openRb = getQueryVariable("rb");
    if (openRb === "true" || openRb === "1") {
      this.startBuilder();
    }
  },
  methods: {
    init: function (config) {
      // console.log("Initializing ERB.");
      // console.log(config.product);

      // Getting availability rules based on variant options
      this.availability = getAvailableVariants(config.product, config.mappings);
      this.getDefaultShape(config);
      this.getDefaultFilters(config);
      this.config = config;

      // setting up api page size
      // console.log("defaultOptions", defaultOptions);
      if (
        defaultOptions &&
        this.config.api &&
        this.config.api.laReserve &&
        this.config.api.laReserve.pageSize
      )
        defaultOptions.pageSize = this.config.api.laReserve.pageSize;

      // checking favorites still exist
      this.checkAvailable3dModels();
      checkFavorites(this.config.api.diamondsApp.baseUrl);

      if (this.config.renderInitDiamondPrice) {
        var urlDiamondPrice = getQueryVariable("d");
        if (urlDiamondPrice) {
          this.renderInitDiamondPrice = true;
          var diamondPrice =
            urlDiamondPrice.indexOf(",") > -1
              ? parseInt(urlDiamondPrice.split(",")[1])
              : 0;

          var baseVariantPrice = getBaseVariantPrice(
            this.selectedOptions,
            this.config.product,
            this.config.mappings
          );
          var totalRenderDiamondPrice =
            parseInt(baseVariantPrice) + parseInt(diamondPrice);
          this.renderDiamondPrice = `Total Ring Price $${formatMoney(
            totalRenderDiamondPrice * 0.01
          )}`;
        }
      }
    },
    updateInstallments: function (dataInstallments) {
      this.broadcast("rb-update-installments", dataInstallments);
    },
    checkSelectedVariant: function () {
      var selectedVariantId = getQueryVariable("variant");
      if (!selectedVariantId) return;
      var selectedVariant = this.config.product.variants.find(
        (variant) => parseInt(variant.id) === parseInt(selectedVariantId)
      );

      if (selectedVariant && selectedVariant.available) {
        var shapeStep = this.config.steps.find((step) => step.id === "shape");
        var selectedVariantShape =
          selectedVariant[
            this.config.mappings.product.options["shape"]
          ].toLowerCase();
        selectedVariantShape =
          selectedVariantShape === "round"
            ? "round brilliant"
            : selectedVariantShape;
        var shapeData = {
          step: this.deleteKey(shapeStep, "choices"),
          choice: shapeStep.choices.find(
            (choice) => choice.label.toLowerCase() === selectedVariantShape
          ),
        };
        // console.log('shapeStep', shapeData, selectedVariant[this.config.mappings.product.options["shape"]]);

        var metalStep = this.config.steps.find((step) => step.id === "metal");
        var metalData = {
          step: this.deleteKey(metalStep, "choices"),
          choice: metalStep.choices.find((choice) => {
            // console.log(choice.label, selectedVariant["option1"]);
            return (
              choice.label.toLowerCase() ===
              selectedVariant[
                this.config.mappings.product.options["metal"]
              ].toLowerCase()
            );
          }),
        };

        // console.log(metalData, shapeData);

        // this.broadcast('rb-')

        this.$nextTick(() => {
          this.broadcast("rb-step-complete", shapeData);
          this.broadcast("rb-step-complete", metalData);
        });
      }
    },
    checkSelectedDiamond: async function () {
      var selectedVariantId = getQueryVariable("variant");
      if (!selectedVariantId) return;

      var selectedVariant = this.config.product.variants.find(
        (variant) => parseInt(variant.id) === parseInt(selectedVariantId)
      );

      if (selectedVariant && selectedVariant.available) {
        var shapeStep = this.config.steps.find((step) => step.id === "shape");
        var selectedVariantShape =
          selectedVariant[
            this.config.mappings.product.options["shape"]
          ].toLowerCase();
        selectedVariantShape =
          selectedVariantShape === "round"
            ? "round brilliant"
            : selectedVariantShape;
        var shapeData = {
          step: this.deleteKey(shapeStep, "choices"),
          choice: shapeStep.choices.find(
            (choice) => choice.label.toLowerCase() === selectedVariantShape
          ),
        };
      }

      if (!shapeData) return;

      var selectedDiamondData = getQueryVariable("d");
      if (!selectedDiamondData) return;

      var selectedDiamondId = selectedDiamondData;
      var selectedDiamondPrice = 0;
      if (selectedDiamondData.indexOf(",") > -1) {
        selectedDiamondId = selectedDiamondData.split(",")?.[0];
        selectedDiamondPrice = selectedDiamondData.split(",")?.[1];
      }

      // Getting Diamonds to Filter and get the one with id or price
      this.paginationSoftReset();
      const allDiamonds = await fetchDiamonds(
        this.config.api.laReserve.baseUrl,
        this.config.api.laReserve.apiEndpoints,
        {
          filters: [
            this.config.filters.filterShapes.find(
              (filterShape) => filterShape.id === shapeData.choice.id
            ),
          ],
          sortBy: { type: "retail_price", mode: "asc" },
          page: 1,
          baseRingPrice: this.baseRingPrice,
        }
      );
      var { diamonds } = allDiamonds;
      if (!diamonds.length) return;

      this.renderInitDiamondPrice = false;

      var selectedDiamondById = diamonds.find(
        (diamond) => diamond.cert_num === selectedDiamondId
      );
      if (!selectedDiamondById) {
        var selectedDiamondByPrice = diamonds.find(
          (diamond) =>
            diamond.retail_price.toString() ===
            (parseInt(selectedDiamondPrice) * 0.01).toString()
        );
      }
      // console.log(selectedDiamondById, selectedDiamondByPrice);

      var selectedDiamond = selectedDiamondById || selectedDiamondByPrice;
      if (!selectedDiamond) return;

      // console.log(selectedDiamond);

      // Broadcasting diamond selections
      var diamondStep = this.config.steps.find((step) => step.id === "diamond");
      var diamondData = {
        step: this.deleteKey(diamondStep, "choices"),
        choice: selectedDiamond,
      };

      this.broadcast("rb-step-complete", diamondData);
    },
    isPrevWhishlist: function () {
      this.broadcast("rb-whishlist-prev");
    },
    isSoldOut: function () {
      this.broadcast("rb-sold-out");
    },
    loadFromDiamondPrice: async function () {
      const priceData = await fetchDiamonds(
        this.config.api.laReserve.baseUrl,
        this.config.api.laReserve.apiEndpoints,
        {
          filters: this.appliedFilters,
          sortBy: this.sortMode,
          page: 1,
        },
        "price"
      );
      //console.log(priceData);
      this.fromDiamondPrice =
        priceData && priceData.length > 0
          ? parseInt(priceData[0].min_price)
          : 0;
    },
    reloadTotalPrice: function () {
      //      console.log("rediaprice");

      var baseVariantPrice = getBaseVariantPrice(
        this.selectedOptions,
        this.config.product,
        this.config.mappings
      );
      this.baseVariantPrice = baseVariantPrice;

      this.broadcast("rb-update-price", {
        baseRingPrice: this.baseRingPrice,
        fromDiamondPrice: this.fromDiamondPrice,
        selectedOptions: this.selectedOptions,
      });
    },
    getNextAvailableStep: function () {
      console.log("this.currentStep", this.currentStep);
      var nextStep = -1;
      for (var i = 0; i < this.config.steps.length; i++) {
        if (
          !this.selectedOptions.find(
            (selectedOption) =>
              selectedOption.step.id === this.config.steps[i].id
          )
        ) {
          nextStep = i;
          break;
        }
      }
      console.log("nextStep", nextStep);
      return nextStep > -1 ? nextStep : this.currentStep + 1;
    },
    /**
     * Global Events
     */
    onNextStep: function () {
      if (this.previousStep !== -1) {
        this.currentStep = this.previousStep;
        this.previousStep = -1;
      } else {
        this.currentStep++;
      }

      // Since we are exposing metal in the product info, we need to skip this step during the selection flow
      if (
        this.currentStep === 2 &&
        this.selectedOptions.find(
          (selectedOption) => selectedOption.step.id === "metal"
        )
      ) {
        this.currentStep = 3;
      }
      // this.currentStep = this.getNextAvailableStep();
      this.broadcast(
        "rb-step-display-update",
        this.config.steps[this.currentStep]
      );
      this.$emit("rb-step-loaded", {
        step: this.config.steps[this.currentStep]?.id,
      });
    },
    onPrevStep: function () {
      this.currentStep--;
      this.broadcast(
        "rb-step-display-update",
        this.config.steps[this.currentStep]
      );
      this.$emit("rb-step-loaded", {
        step: this.config.steps[this.currentStep]?.id,
      });
    },
    onUpdateStep: function () {
      this.currentStep = 6;
      this.broadcast(
        "rb-step-display-update",
        this.config.steps[this.currentStep]
      );
      this.$emit("rb-step-loaded", {
        step: this.config.steps[this.currentStep]?.id,
      });
    },
    onStepComplete: function (data) {
      // console.log('onstepcomplete', data);
      this.addSelectedOption(data);
      this.reloadTotalPrice();
      this.availability = getAvailableVariants(
        this.config.product,
        this.config.mappings
      );
    },
    onStepIncomplete: function (data) {
      this.removeSelectedOption(data);
      this.reloadTotalPrice();
    },
    onStepEdit: function (data) {
      if (data.from && data.from === "widget")
        this.previousStep = this.currentStep;
      this.editStep(data.id);
    },
    onOpenFilters: function () {
      this.broadcast("rb-open-popup", {
        id: "rb-popup-filters",
        appliedFilters: this.appliedFilters,
      });
    },
    onUpdateFilters: function (filter) {
      this.addAppliedFilter(filter);
    },
    onRemoveFilters: function (filter) {
      this.addAppliedFilter(
        filter,
        filter.type === "retail_price" || filter.type === "carats"
      );
      this.broadcast("rb-apply-filters", this.appliedFilters);
    },
    onApplyFilters: function (appliedFilters) {
      this.syncAppliedFilterWithSelectedOption();
      this.$nextTick(() => {
        // console.log("appliedFilters app", appliedFilters);
        this.appliedFilters = appliedFilters;

        this.paginationHardReset();
        this.clearDiamonds();
        // this.broadcast("rb-load-diamonds");
        this.broadcast("rb-load-next-page");
      });
    },
    onSort: function (sortMode) {
      this.sortMode = sortMode;
      this.broadcast("rb-sorted", sortMode);
      this.paginationHardReset();
      this.clearDiamonds();
      // this.broadcast("rb-load-diamonds");
      this.broadcast("rb-load-next-page");
    },
    onLoadNextPage: function () {
      this.paginationNextPage();
      this.broadcast("rb-load-diamonds");
    },
    onLoadDiamonds: function () {
      this.loadDiamonds();
    },
    onSaveForLater: function () {
      // console.log("saving for later");
      saveForLater(this.selectedOptions);
    },
    onSaveForLaterDelete: function () {
      deleteSavedForLater();
      this.selectedOptions = this.selectedOptions.filter(
        (selectedOption) => selectedOption.step.id === "shape"
      );
      this.currentStep = 0;
    },
    onWhishlist: function () {
      this.$emit("rb-whishlist", { product: this.config.product });
    },
    onProductDetails: function () {
      this.$emit("rb-product-details");
    },
    onAddToBag: function (cartItems) {
      // console.log("on add to bag inner");

      this.$emit("rb-add-to-bag", { data: cartItems });
    },
    onOpenDiamondExplorer: function () {
      this.$emit("rb-open-diamond-explorer");
    },
    onCloseDiamondExplorer: function () {
      this.$emit("rb-close-diamond-explorer");
    },
    onDropAHint: function () {
      this.$emit("rb-drop-a-hint");
    },
    onYotpoReviews: function () {
      this.$emit("rb-yotpo-reviews");
    },
    onTotalPriceUpdate: function (dataPrice) {
      this.$emit("rb-total-price-update", dataPrice);
    },
    onPrequalifyNow: function () {
      this.$emit("rb-prequalify-now");
    },
    onAcuity: function (url) {
      this.$emit("rb-acuity", url);
    },
    onTrack: function (dataTrack) {
      //console.log("tracking", dataTrack);
      this.$emit("rb-track", dataTrack);
    },
    onNotifyClosePopup: function (data) {
      this.$emit("rb-close-popup", data);
    },
    onNotifyOpenPopup: function (data) {
      this.$emit("rb-open-popup", data);
    },
    initEvents: function () {
      this.subscribe("rb-start-builder", this.startBuilder);
      this.subscribe("rb-end-builder", this.endBuilder);
      this.subscribe("rb-next-step", this.onNextStep);
      this.subscribe("rb-prev-step", this.onPrevStep);
      this.subscribe("rb-update-step", this.onUpdateStep);
      this.subscribe("rb-step-complete", this.onStepComplete);
      this.subscribe("rb-step-incomplete", this.onStepIncomplete);
      this.subscribe("rb-step-edit", this.onStepEdit);
      this.subscribe("rb-open-filters", this.onOpenFilters);
      this.subscribe("rb-update-filters", this.onUpdateFilters);
      this.subscribe("rb-remove-filters", this.onRemoveFilters);
      this.subscribe("rb-apply-filters", this.onApplyFilters);
      this.subscribe("rb-sort", this.onSort);
      this.subscribe("rb-load-next-page", this.onLoadNextPage);
      this.subscribe("rb-load-diamonds", this.onLoadDiamonds);
      this.subscribe("rb-save-for-later", this.onSaveForLater);
      this.subscribe("rb-save-for-later-delete", this.onSaveForLaterDelete);
      this.subscribe("rb-whishlist", this.onWhishlist);
      this.subscribe("rb-add-to-bag", this.onAddToBag);
      this.subscribe("rb-product-details", this.onProductDetails);
      this.subscribe("rb-open-diamond-explorer", this.onOpenDiamondExplorer);
      this.subscribe("rb-close-diamond-explorer", this.onCloseDiamondExplorer);
      this.subscribe("rb-drop-a-hint", this.onDropAHint);
      this.subscribe("rb-yotpo-reviews", this.onYotpoReviews);
      this.subscribe("rb-total-price-update", this.onTotalPriceUpdate);
      this.subscribe("rb-prequalify-now", this.onPrequalifyNow);
      this.subscribe("rb-acuity", this.onAcuity);
      this.subscribe("rb-track", this.onTrack);
      // These are added for Tangiblee
      this.subscribe("rb-notify-open-popup", this.onNotifyOpenPopup);
      this.subscribe("rb-notify-close-popup", this.onNotifyClosePopup);
    },
    /**
     * End Global Events
     */
    selectDefaultShape: function () {
      var shapeStep = this.config.steps.find((step) => step.id === "shape");
      this.broadcast("rb-step-complete", {
        step: this.deleteKey(shapeStep, "choices"),
        choice: shapeStep.choices.find((choice) => choice.default),
      });
    },
    clearDiamonds: function () {
      this.diamonds = [];
      this.diamondsMeta = null;
    },
    loadDiamonds: async function () {
      this.broadcast("rb-loading-diamonds");
      const allDiamonds = await fetchDiamonds(
        this.config.api.laReserve.baseUrl,
        this.config.api.laReserve.apiEndpoints,
        {
          filters: this.appliedFilters,
          sortBy: this.sortMode,
          page: this.paginationCurrentPage,
          baseRingPrice: this.baseRingPrice,
        }
      );

      // keeps track of duplicated diamonds
      this.deduplicateAddToStack(allDiamonds.diamonds);

      var { diamonds, meta } = allDiamonds;
      this.diamonds = [...this.diamonds, ...this.deduplicateDiamonds(diamonds)];
      this.diamondsMeta = meta;

      this.broadcast("rb-loaded-diamonds");
    },
    startBuilder: function () {
      this.showFullscreen = true;
      this.showFooter = true;
      this.broadcast("rb-open-fullscreen");
      this.$emit("rb-open-fullscreen");
      this.$nextTick(() => {
        this.broadcast("rb-next-step");
      });
    },
    endBuilder: function () {
      this.showFullscreen = false;
      this.showFooter = true;
      this.currentStep = 0;
      this.broadcast("rb-close-fullscreen");
      this.$emit("rb-close-fullscreen");
    },
    editStep: function (id) {
      this.currentStep = this.config.steps.findIndex((step) => step.id === id);
      this.$emit("rb-step-loaded", {
        step: this.config.steps[this.currentStep]?.id,
      });
    },
    addSelectedOption: function (option) {
      this.selectedOptions = [
        ...this.selectedOptions.filter(
          (selectedOption) => selectedOption.step.id !== option.step.id
        ),
        option,
      ];
      // console.log(this.selectedOptions);
      this.syncSelectedOptionWithAppliedFilter(option);
    },
    removeSelectedOption: function (option) {
      this.selectedOptions = [
        ...this.selectedOptions.filter(
          (selectedOption) => selectedOption.step.id !== option.step.id
        ),
      ];
    },
    updateLayout: function () {
      this.showGallery = true;
      this.showStepFullscreen = false;
      this.showFooter = true;
      if (this.config.steps[this.currentStep].id === "diamond") {
        this.showGallery = false;
        this.showStepFullscreen = true;
      }
      if (this.config.steps[this.currentStep].id === "summary") {
        this.showFooter = false;
        this.showGallery = true;
      }
    },
    addAppliedFilter: function (filter, forceRemove = false) {
      if (filter.type === "shape") {
        this.appliedFilters = [
          ...this.appliedFilters.filter(
            (appliedFilter) => appliedFilter.type !== "shape"
          ),
          filter,
        ];
      } else if (filter.type === "retail_price") {
        if (!forceRemove) {
          this.appliedFilters = [
            ...this.appliedFilters.filter(
              (appliedFilter) => appliedFilter.type !== "retail_price"
            ),
            filter,
          ];
        } else {
          this.appliedFilters = [
            ...this.appliedFilters.filter(
              (appliedFilter) => appliedFilter.type !== "retail_price"
            ),
          ];
        }
      } else if (filter.type === "carats") {
        if (!forceRemove) {
          this.appliedFilters = [
            ...this.appliedFilters.filter(
              (appliedFilter) => appliedFilter.type !== "carats"
            ),
            filter,
          ];
        } else {
          this.appliedFilters = [
            ...this.appliedFilters.filter(
              (appliedFilter) => appliedFilter.type !== "carats"
            ),
          ];
        }
      } else {
        var existingAppliedFilter = this.appliedFilters.find(
          (appliedFilter) =>
            appliedFilter.id === filter.id && appliedFilter.type === filter.type
        );
        if (existingAppliedFilter) {
          this.appliedFilters = [
            ...this.appliedFilters.filter(
              (appliedFilter) =>
                !(
                  appliedFilter.type === filter.type &&
                  appliedFilter.id === filter.id
                )
            ),
          ];
        } else {
          this.appliedFilters = [
            ...this.appliedFilters.filter(
              (appliedFilter) =>
                !(
                  appliedFilter.type === filter.type &&
                  appliedFilter.id === filter.id
                )
            ),
            filter,
          ];
        }
      }
    },
    syncSelectedOptionWithAppliedFilter: function (option) {
      // console.log('option', option);
      // if what changed is the shape, also update the appliedFilter
      if (option.step.id === "shape") {
        this.appliedFilters = [
          ...this.appliedFilters.filter(
            (appliedFilter) => appliedFilter.type !== "shape"
          ),
          this.config.filters.filterShapes.find(
            (filter) => filter.title === option.choice.label
          ),
        ];
      }
    },
    syncAppliedFilterWithSelectedOption: function () {
      // updating selected options based on the shape filter
      var appliedFilterShape = this.appliedFilters.find(
        (appliedFilter) => appliedFilter.type === "shape"
      );
      var shapeStep = this.config.steps.find((step) => step.id === "shape");
      this.selectedOptions = [
        ...this.selectedOptions.filter(
          (selectedOption) => selectedOption.step.id !== "shape"
        ),
        {
          step: this.deleteKey(shapeStep, "choices"),
          choice: shapeStep.choices.find(
            (choice) => choice.label === appliedFilterShape.title
          ),
        },
      ];
    },
    deleteKey: function (obj, key) {
      return deleteKey(obj, key);
    },
    checkSavedForLater: function () {
      var dataSavedForLater = getSavedForLater();
      if (dataSavedForLater && dataSavedForLater.length) {
        this.comesFromSaveForLater = true;
        this.selectedOptions = dataSavedForLater;
        this.currentStep = 6;
      }
    },
    getDefaultFilters: function (config) {
      // filters
      var shapeAvailability = this.availability.find(
        (av) => av.stepId === "shape"
      );
      var defaultShapeFilter = [...config.filters.filterShapes].find(
        (filterShape) =>
          parseInt(filterShape.id) === parseInt(shapeAvailability.available[0])
      );
      if (defaultShapeFilter) {
        this.appliedFilters = [{ ...defaultShapeFilter, type: "shape" }];
      } else {
        this.appliedFilters = [
          { ...config.filter.filterShapes[0], type: "shape" },
        ];
      }
      config.filters.filterShapes = [...config.filters.filterShapes].filter(
        (filterShape) =>
          shapeAvailability.available.find(
            (av) => parseInt(av) === parseInt(filterShape.id)
          )
            ? true
            : false
      );
    },
    getDefaultShape: function (config) {
      var defaultShape = config.steps
        .find((step) => step.id === "shape")
        ?.choices.find((choice) => choice.default);
      var shapeAvailability = this.availability.find(
        (av) => av.stepId === "shape"
      );
      if (shapeAvailability) {
        defaultShape = config.steps
          .find((step) => step.id === "shape")
          ?.choices.find(
            (choice) =>
              parseInt(choice.id) === parseInt(shapeAvailability.available[0])
          );
      }
      if (defaultShape) {
        var stepShape = config.steps.find((step) => step.id === "shape");
        stepShape.choices = [
          ...stepShape.choices.map((choice) => ({
            ...choice,
            default: choice.id === defaultShape.id,
          })),
        ];
      }
    },
    checkAvailable3dModels: async function () {
      if (
        this.config.product.metafields_custom &&
        this.config.product.metafields_custom.models3d
      ) {
        this.availableModels = await getAvailable3dModels(
          this.config.api.modelViewer.baseJsonUrl,
          this.baseModel,
          this.availability,
          this.config.mappings,
          this.config.product.metafields_custom.models3d.split(",") || []
        );
      } else {
        this.availableModels = await checkAvailable3dModels(
          this.config.api.modelViewer.baseJsonUrl,
          this.baseModel,
          this.availability,
          this.config.mappings
        );
      }

      this.$nextTick(() => {
        this.broadcast("rb-available-models", {
          selectedOptions: this.selectedOptions,
          mappings: this.config.mappings,
        });
      });
      // console.log(this.availableModels);
    },
    updateQueryVariant: function () {
      var selectedVariants = getSelectedVariants(
        this.selectedOptions,
        this.config.product,
        this.config.mappings
      );
      var newUrl = createVariantUrl(selectedVariants[0]?.id);
      if (newUrl) window.history.replaceState({ path: newUrl }, "", newUrl);

      this.$emit("rb-variant-change", { variantId: selectedVariants[0]?.id });
      this.broadcast("rb-variant-change", selectedVariants[0]);
    },
    paramsToObject: function (entries) {
      const result = {};
      for (const [key, value] of entries) {
        // each 'entry' is a [key, value] tupple
        result[key] = value;
      }
      return result;
    },
  },
};
</script>
